import { Box } from "@chakra-ui/react";
import type { LucideIcon } from "lucide-react";
import { Link, useMatch } from "react-router-dom";

type Props = {
  path: string;
  section: string;
  showText: boolean;
  icon: LucideIcon;
  onClick?: () => void;
  disableActiveStyle?: boolean;
  showBadge?: boolean;
  isDisabled?: boolean;
  tooltipText?: string;
  /** Tour anchor override for action items, whose path is just "#". */
  tourId?: string;
  tag?: string;
};

const NavigationSection = ({
  path,
  section,
  showText,
  icon: Icon,
  onClick,
  disableActiveStyle,
  showBadge = false,
  isDisabled = false,
  tourId,
  tag,
}: Props): JSX.Element => {
  const isActive = useMatch({ path, end: false });

  return (
    <Link
      to={path}
      data-tour={tourId ?? path}
      style={{ textDecoration: "none" }}
      onClick={isDisabled ? undefined : onClick}
    >
      <Box
        px={3}
        py={2}
        display={"flex"}
        alignItems={"center"}
        fontWeight={600}
        gap={showText ? 2 : 0}
        borderRadius={"md"}
        pointerEvents={isDisabled ? "none" : "auto"}
        opacity={isDisabled ? 0.5 : 1}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        bg={isActive && !disableActiveStyle ? "palomma.500" : "transparent"}
        color={
          isActive && !disableActiveStyle
            ? "palomma.100"
            : disableActiveStyle
            ? "inherit"
            : "gray.600"
        }
        _hover={
          isDisabled
            ? {}
            : {
                bg:
                  isActive && !disableActiveStyle
                    ? "palomma.500/92"
                    : "gray.100",
              }
        }
        transition="all 0.2s"
      >
        <Box position="relative">
          <Icon size={20} strokeWidth={2} />
          {showBadge && !isDisabled && (
            <Box
              position="absolute"
              zIndex="1"
              top="-5px"
              right="-5px"
              borderRadius="full"
              width="10px"
              height="10px"
              padding={0}
              bgColor="#EC5858"
            />
          )}
        </Box>

        <Box
          overflow="hidden"
          transition="all 0.3s ease-in-out"
          width={showText ? "auto" : "0px"}
          m={0}
          opacity={showText ? 1 : 0}
          whiteSpace="nowrap"
          fontSize="sm"
        >
          {section}
        </Box>
        {tag && showText && (
          <Box
            ml="auto"
            px={2}
            py={0.5}
            borderRadius="full"
            fontSize="2xs"
            fontWeight={700}
            letterSpacing="wider"
            textTransform="uppercase"
            bg={
              isActive && !disableActiveStyle ? "whiteAlpha.300" : "palomma.100"
            }
            color={isActive && !disableActiveStyle ? "white" : "palomma.500"}
          >
            {tag}
          </Box>
        )}
      </Box>
    </Link>
  );
};

export default NavigationSection;
